import Commons from "oc-commons/Commons"
import Wkfg2BackendApi from "oc-app/Wkfg2BackendApi";
import {Unresolved} from "./validation/Unresolved";
import {TableValidState} from "./validation/TableValidState";
import {RowValidState} from "./validation/RowValidState";
import {TransSource} from "./validation/TransSource";
import {CellValidState} from "./validation/CellValidState";
import AbstractRichMetadata from "../AbstractRichMetadata";
import Wkfg2TypeConverter from "../Wkfg2TypeConverter";

/**
 * Folgende Features:
 * - Validiert alle Daten des Metamodells
 * - wandelt ForeignKeys im Hauptspeicher in direkte Objekt-Referenzen (auch Back-Referenzen) um
 * - führt das expansion - Feature aus
 * - führt das subtable - Feature aus
 */
export class ValidationMetadata extends AbstractRichMetadata {

    /**
     * nach wie vielen ms am Stück legt sich die ValidationMetadata preemptiv schlafen?
     */
    static sleepAfterWorkMs = 3;

    /**
     * wie lange pausiert das ValidationMetadata, wenn es schläft, um dem Mainthread zeit zu geben?
     * @type {number}
     */
    static sleepMs = 3;

    /**
     * nach der Validierung stehen hier die Infos, welche Einträge invalide sind, und warum
     *
     * @type {Map<MetaTable, TableValidState>}
     */
    tableRowColValidStateMap = new Map();

    /**
     * @type {{[tableName]: primaryKeyName}}
     */
    _primaryKeyOfTable = {};

    /**
     * @type {{[tableName]: MetaTable}}
     */
    metaTableMap = {};

    /**
     * row=>APP.metamodel
     * @type {WeakMap<object, json>}
     */
    rowToMetatableMap = new WeakMap();

    constructor(metadata) {
        super(metadata);
    }

    /**
     * gibt den ValidState entweder für die ganze Tabelle, für eine ganze Row,
     * oder für einen Wert zurück
     * @return {null|any}
     */
    getValidState(tableName, rowId = undefined, columnName = undefined) {
        const tableValidState = this.tableRowColValidStateMap.get(tableName);
        if (!tableValidState) {
            return null;
        }

        if (rowId === undefined) {
            return tableValidState;
        }

        const rowValidState = tableValidState.rowMap.get(rowId);
        if (!rowValidState) {
            return null;
        }

        if (columnName === undefined) {
            return rowValidState;
        }

        return rowValidState.columnMap.get(columnName);
    }

    async loadAndInit() {
        this._sleepCount=0;
        this.metadata = await new Wkfg2BackendApi(APP.settings.wkfgBackendUrl).loadMetadata();
        const t0 = performance.now();
        await this.init();
        const t1 = performance.now();
        console.warn(`metadata took ${t1 - t0} ms and slept for ${this._sleepMsSum} ms`);
        APP.trigger('metadata-ready');
    }

    async init() {
        this._registerMetaTables();
        this._fillRowToMetatableMap();
        this._addStaticBackrefToMetamodel();
        this._addColumnMapToMetamodel();
        this._sortTables();
        this._initPrimaryKeyOfTables();
        this._doExpansions();
        this._splitMultiForeignKeys();
        await this._prepareForeignKeyCache();
        await this._resolveForeignKeysByReferences();
        await this._resolveBackReferences();
        await this._validateRows();
        this._generateSubTables();
        await this._doTypeConverter();
    }

    async _validateRows() {
        const maybeTextJoin = text => _.isArray(text) ? text.join('') : text;

        const compileEnabledIf = (table, column) => {
            let code = maybeTextJoin(column.enabledIf);
            const fnBody = 'with (_row_) { return ' + code + '; }';
            try {
                const enabledFn = new Function('_row_', 'metadata', 'validation', fnBody);
                column.enabledIfFunction = (_row_) => {
                    if (_row_ == null) {
                        return null;
                    }
                    try {
                        return enabledFn(_row_, this.metadata, this);
                    } catch (e) {
                        console.error('error in evaluating column.enabledIf: table=' + table.name + ' column=' + column.name + ' code="' + fnBody + '"', '_row_=', _row_, 'error=', e);
                        return null;
                    }
                }
            } catch (e) {
                console.error('error in compiling column.enabledIf: table=' + table.name + ' column=' + column.name + ' code="' + fnBody + '"', e);
            }
        };

        const compileValidIf = (table, column) => {
            let code = maybeTextJoin(column.validIf);
            const fnBody = 'with (_row_) { return ' + code + '; }';
            try {
                const validFn = new Function('_row_', 'metadata', 'validation', fnBody);
                column.validIfFunction = (_row_) => {
                    if (_row_ == null) {
                        return null;
                    }
                    try {
                        return validFn(_row_, this.metadata, this);
                    } catch (e) {
                        console.error('error in evaluating column.validIf: table=' + table.name + ' column=' + column.name + ' code="' + fnBody + '"', '_row_=', _row_, 'error=', e);
                        debugger;
                        validFn(_row_, this.metadata);
                        return null;
                    }
                }
            } catch (e) {
                console.error('error in compiling column.validIf: table=' + table.name + ' column=' + column.name + ' code="' + fnBody + '"', e);
            }
        };

        const compileRegexp = (table, column) => {
            try {
                column.validationRegExpCompiled = new RegExp(column.validationRegExp, "g");
            } catch (e) {
                console.error('error in compiling column.validIf: table=' + table.name + ' column=' + column.name + ' code="' + fnBody + '"', e);
            }
        }

        const precompileColumn = (table, column) => {
            if (column.validationRegExp != null) {
                compileRegexp(table, column);
            }
            if (column.validIf != null) {
                compileValidIf(table, column);
            }
            if (column.enabledIf != null) {
                compileEnabledIf(table, column);
            }
            if (column.enabledIf != null) {
                compileEnabledIf(table, column);
            }
            if (column.validateFileExists && column.directoryContent) {
                column.directoryContentSet = new Set(column.directoryContent);
            }
        }

        /**
         * hier findet die zentrale Validierung der MetamodellColumn statt
         * @param row
         * @param column
         * @return {CellValidState|null}
         */
        const validate = (row, column) => {
            const value = row[column.name];

            if (column.enabledIfFunction != null && !column.enabledIfFunction(row)) {
                return new CellValidState(true, 'crud_disabled', undefined);
            }

            if (Commons.isEmpty(value)) {
                if (column.required) {
                    return new CellValidState(false, 'crud_invalid', I18N('value is required'));
                }
                return null;
            }

            if (column.foreignKey) {
                const isArray = Array.isArray(value);
                const keyArray = isArray ? value : [value];
                const unresolveds = keyArray.filter(v => v instanceof Unresolved);
                if (unresolveds.length > 0) {
                    row[column.name] = isArray ? keyArray.filter(v => !(v instanceof Unresolved)) : null;
                    return new CellValidState(false, 'crud_invalid', I18N('missing foreign keys %s', unresolveds.map(v => v.key).join(', ')));
                }
            }

            if (column.min != null && value < column.min) {
                return new CellValidState(false, 'crud_invalid', I18N('value < %s', column.min));
            }

            if (column.max != null && value > column.max) {
                return new CellValidState(false, 'crud_invalid', I18N('value > %s', column.max));
            }

            if (column.validationRegExpCompiled != null) {
                column.validationRegExpCompiled.lastIndex = 0; // wegen "g" - Flag notwendig
                if (!column.validationRegExpCompiled.test(value)) {
                    return new CellValidState(false, 'crud_invalid', I18N('does not match pattern %s', column.validationRegExp.toString()));
                }
            }

            if (column.validIfFunction != null) {
                const result = column.validIfFunction(row);
                if (result !== true) {
                    if (_.isString(result)) {
                        return new CellValidState(false, 'crud_invalid', result);
                    } else {
                        return new CellValidState(false, result.className, result.message);
                    }
                }
            }

            if (column.directoryContentSet!=null) {
                if (!value.contains('://')) {
                    if (!column.directoryContentSet.has(value)) {
                        return new CellValidState(false, 'crud_invalid', I18N('file does not exist'));
                    }
                }
            }

            if (column.fkExists) {
                const {tableName,columnName} = column.fkExists.split('.');
                const table = this.metadata[tableName];
                if (table) {
                    if (table.some(fkRow=>fkRow[columnName]===row)) {
                        return new CellValidState(false, 'crud_transitive', I18N('there must be a referencing valid entry in %s', tableName, columnName));
                    }
                }
            }

            return null;
        }

        const createTableValidState = (tableName) => {
            const tableValidState = new TableValidState();
            this.tableRowColValidStateMap.set(tableName, tableValidState);
            return tableValidState;
        }

        const createRowValidState = (tableValidState, rowId) => {
            const rowValidState = new RowValidState();
            tableValidState.rowMap.set(rowId, rowValidState);
            return rowValidState;
        }

        APP.metamodel.tables.forEach(metaTable => {
            const table = this.metadata[metaTable.name];
            if (table) {
                metaTable.columns.forEach(column => precompileColumn(metaTable, column));
            }
        });

        // brean-dead über alles iterieren bis nur noch valid übrigbleiben, kann man vielleicht
        // optimieren..
        let iterationCounter = 0;
        while (iterationCounter < 20) {

            // markiere alle neuen invaliden Rows dieser Iteration

            // console.log('_removeInvalidRows iterationCounter ',iterationCounter++);
            const invalidRows = new Set();
            const invalidTables = new Set();
            const rowToTableNameMap = new Map();
            for (const metaTable of APP.metamodel.tables) {
                const table = this.metadata[metaTable.name];
                if (table) {
                    await this._maybeSleepAtTable(table);
                    let tableValidState = this.tableRowColValidStateMap.get(metaTable.name);
                    table.forEach(row => {
                        rowToTableNameMap.set(row, metaTable.name);
                        let rowValidState = tableValidState ? tableValidState.rowMap.get(row.id) : null;
                        for (const column of metaTable.columns) {
                            let columnValidState = rowValidState ? rowValidState.columnMap.get(column.name) : null;
                            if (columnValidState == null) {
                                const validState = validate(row, column);
                                if (validState != null) {
                                    tableValidState = tableValidState || createTableValidState(metaTable.name);
                                    rowValidState = rowValidState || createRowValidState(tableValidState, row.id);
                                    rowValidState.columnMap.set(column.name, validState);
                                    if (!validState.valid) {
                                        invalidRows.add(row);
                                        invalidTables.add(metaTable.name);
                                        tableValidState.valid = false;
                                        rowValidState.valid = false;
                                    }
                                }
                            }
                        }
                    });
                }
            }

            if (invalidRows.length === 0) {
                // console.log('_removeInvalidRows everything valid => exit');
                return;
            }

            // entferne alle invaliden Rows aus sämtlichen Collections, d.h. alle direkten
            // Referenzen und alle Back-Referenzen, und erkenne transitiv invalide Rows

            invalidRows.forEach(invalidRow => {
                Object.keys(invalidRow).forEach(rowCol => {
                    const value = invalidRow[rowCol];
                    this._forOneOrEach(value)(related => {
                        if (_.isPlainObject(related)) {
                            Object.keys(related).forEach(relatedCol => {
                                let isReferenceOnInvalid = false;
                                if (related[relatedCol] === invalidRow) {
                                    related[relatedCol] = null;
                                    isReferenceOnInvalid = true;
                                } else if (Array.isArray(related[relatedCol])) {
                                    const index = related[relatedCol].indexOf(invalidRow);
                                    if (index >= 0) {
                                        related[relatedCol].splice(index, 1);
                                        isReferenceOnInvalid = true;
                                    }
                                }
                                if (isReferenceOnInvalid) {
                                    const tableName = rowToTableNameMap.get(related);
                                    const isRealColumn = this.getMetaTable(tableName).columns.some(c => c.name === relatedCol && c.type!=='Backref');
                                    // wenn isRealColumn==false, dann ist es eine Backreference,
                                    // aber die führt nicht zu einer Fehleranzeige
                                    if (isRealColumn) {
                                        const tableValidState = this.tableRowColValidStateMap.get(tableName) || createTableValidState(tableName);
                                        const rowValidState = tableValidState.rowMap.get(related.id) || createRowValidState(tableValidState, related.id);
                                        let columnValidState = rowValidState.columnMap.get(relatedCol);
                                        if (columnValidState == null) {
                                            const invalidTableName = rowToTableNameMap.get(invalidRow);
                                            const invalidRowState = this.tableRowColValidStateMap.get(invalidTableName).rowMap.get(invalidRow.id);

                                            tableValidState.transValid = false;
                                            rowValidState.transValid = false;

                                            if (invalidRowState.transValid) {
                                                const column = _.first(invalidRowState.columnMap.map((v, k) => k));
                                                rowValidState.transSource = new TransSource(invalidRow.Key, invalidTableName, column);
                                            } else {
                                                rowValidState.transSource = invalidRowState.transSource;
                                            }

                                            columnValidState = new CellValidState(false, 'crud_transitive',
                                                I18N('transitive invalid: %s',
                                                    invalidRow.Key,
                                                    rowValidState.transSource.Key,
                                                    rowValidState.transSource.tableName,
                                                    rowValidState.transSource.column));
                                            rowValidState.columnMap.set(relatedCol, columnValidState);

                                            invalidRows.add(related);
                                            invalidTables.add(tableName);
                                        }
                                    }
                                }
                            });
                        }
                    });
                });
            });

            invalidTables.forEach(tableName => {
                this.metadata[tableName] = this.metadata[tableName].filter(row => !invalidRows.has(row));
            });

        }
    }

    _fillRowToMetatableMap() {
        APP.metamodel.tables.forEach(metaTable=> {
            const table = this.metadata[metaTable.name];
            if (table) {
                table.forEach(row=>
                    this.rowToMetatableMap.set(row,metaTable));
            }
        });
    }

    /**
     * führt das sortyBy Feature aus
     */
    _sortTables() {
        APP.metamodel.tables.forEach(metaTable=> {
            const sortName = metaTable.sortBy;
            if (sortName) {
                const table = this.metadata[metaTable.name];
                if (table) {
                    _.sortBy(table,sortName);
                }
            }
        });
    }

    /**
     * implementiert das SubTables-Feature. Alle Tabellen mit dem Attribut generateSubTableColumn
     * werden im metamodell in einzelne Tabellen aufgespaltet
     */
    _generateSubTables() {
        APP.metamodel.tables.filter(metaTable=>metaTable.generateSubTableColumn).forEach(metaTable=>{
            const subtableColumn = metaTable.columns.find(c=>c.name==metaTable.generateSubTableColumn && c.type!=='Backref');
            const table = this._getTable(metaTable.name);
            table.forEach(row=>{
                const subTableColumnValue = row[subtableColumn.name];
                if (Commons.isEmpty(subTableColumnValue)) {
                    return;
                }
                const subTablePostfix = metaTable.generateSubTablePostix ? '_' + metaTable.name : '';
                const key = subTableColumnValue?.Key || subTableColumnValue;
                const subTableName = key + subTablePostfix;
                let subtable = this.metadata[subTableName] = this.metadata[subTableName] || [];
                subtable.push(row);
            });
        });
    }

    /**
     * Implementierung des expansion-Featurs.
     * Eine Tabelle A.expansion="B" erweitert alle Einträge in B so dass b.A = [a] wenn
     * a.PrimaryKey==b.PrimaryKey, d.h. die Einträge aus A werden in die entsprechenden Einträgen
     * aus B verlinkt
     */
    _doExpansions() {
        APP.metamodel.tables.filter(metaTable => metaTable.expansion).forEach(metaTable => {
            const expandedTable = this.getMetaTable(metaTable.expansion);
            const pkName = this._getPrimaryKeyName(expandedTable.name);

            // FIXME durch bessere Implementierung ersetzen
            const findExpanded = this._getFindMethod(expandedTable.name,pkName);

            const table = this._getTable(metaTable.name);
            table.forEach(row => {
                const expandedRow = findExpanded(row[pkName]);
                if (expandedRow == null) {
                    console.warn(`row with ${pkName} ${row[pkName]} from table ${metaTable.name} expands table ${expandedTable.name} but no matching row with same ${pkName} found`);
                    return;
                }
                expandedRow[metaTable] = expandedRow[metaTable] || [];
                expandedRow[metaTable].push(row);
            });
        });
    }

    /**
     * splittet multiselect / variantKeys in Array von Einzel-Strings
     */
    _splitMultiForeignKeys() {
        APP.metamodel.tables.forEach(metaTable => {
            metaTable.columns.filter(column => column.foreignKey != null && this._isOne2Many(column) && !this._fkNeverResolve(column)).forEach(column => {
                const table = this._getTable(metaTable.name);
                table.forEach(row => {
                    const string = row[column.name];
                    row[column.name] = string == null || string === ''
                        ? []
                        : string.split(/\s*,\s*/).filter(v => v);
                });
            });
        });
    }

    /**
     * mappt ForeignKeys auf Einträge oder Unresolved, speichert diese in einem Cache pro Column
     * muss von _resolveForeignKeysByReferences erfolgen, weil das Auflösen von
     * column.foreignKeyFilter unresolvte Daten voraussetzt
     */
    async _prepareForeignKeyCache() {
        const createForeignKeyFinder = (column) => {
            let fkCondition = (row) => true;

            if (!Commons.isEmpty(column.foreignKeyFilter)) {
                // ForeignKeyFilter kann sich nur auf eine Tabelle beziehen, d.h. foreignKey
                // enthält kein "," ForeignKeyFilter kann komplexer aufgebaut sein, aber hier
                // unterstützen wir nur den einfachsten Fall
                const [quantor, compareColumnName, comparator, compareValue] = column.foreignKeyFilter[0];
                if (quantor == 'all' && comparator == '=') {
                    fkCondition = (row) => row[compareColumnName] == compareValue;
                }
            }

            const foreignKeys = column.foreignKey.split(',').map(key => key.split('.'));

            return key => {
                for (let [fkTable, fkColumn] of foreignKeys) {
                    const rows = this._getTable(fkTable);
                    const result = rows.find(row => row[fkColumn] == key && fkCondition(row));
                    if (result) {
                        return result;
                    }
                }
                return new Unresolved(key);
            }
        }

        await this._resolveForeignKeys((metaTable, table, mapFunction, column) => {
            const cache = column._foreignKeyCache = new Map();
            const findFk = createForeignKeyFinder(column);
            const fillCache = (key) => {
                const value = cache.get(key);
                if (value !== undefined) {
                    return value;
                }
                cache.set(key, findFk(key));
            };
            table.forEach(row => {
                mapFunction(row[column.name], fillCache);
            });
        });

    }

    /**
     * ersetzt für alle Tabellen die ForeignKeys mit den von ihnen referenzierten RowData Objekten
     * (Für Array von FK werden die FKs im Array ersetzt).
     *
     * table.id.foreignKeyCellString |-> foreignTable.row
     */
    async _resolveForeignKeysByReferences() {
        await this._resolveForeignKeys((metaTable, table, mapFunction, column) => {
            const resolveEntityOrString = (key) => column._foreignKeyCache.get(key);
            table.forEach(row => {
                row[column.name] = mapFunction(row[column.name], resolveEntityOrString);
            });
        });
    }

    /**
     */
    async _resolveBackReferences() {
        await this._resolveForeignKeys((metaTable, table, mapFunction, column) => {
            table.forEach(row => {
                mapFunction(row[column.name], value => {
                    if (value instanceof Unresolved) {
                        return;
                    }
                    const backRefName = metaTable.name;
                    if (value[backRefName] == null) {
                        value[backRefName] = [];
                    }
                    let back = value[backRefName];
                    if (back.push) {
                        back.push(row);
                    }
                });
            });
        });
    }

    /**
     * @param inner = (metaTable,table,mapFunction,column) => ...
     */
    async _resolveForeignKeys(inner) {
        for (const metaTable of APP.metamodel.tables) {
            const fkColumns = metaTable.columns.filter(column => column.foreignKey != null && !this._fkNeverResolve(column));
            for (const column of fkColumns) {
                const table = this._getTable(metaTable.name);
                const isOne2Many = column.multiselect || column.variantKey;
                const mapFunction = isOne2Many
                    ? (array, callback) => array.map(callback).filter(string => !(string == null || string === ''))
                    : (string, callback) => string == null || string === '' ? null : callback(string);
                await this._maybeSleepAtTable(table);
                inner(metaTable, table, mapFunction, column);
            }
        }
    }


    _initPrimaryKeyOfTables() {
        APP.metamodel.tables.forEach(table => {
            const primary = table.columns.find(c => c.primaryKey);
            if (primary) {
                this._primaryKeyOfTable[table.name] = primary.name;
            } else if (!Commons.isEmpty(table.uniqueIndex)) {
                this._primaryKeyOfTable[table.name] = _.first(table.uniqueIndex.filter(s => s != 'App'));
            }
        });
    }

    async _doTypeConverter() {
        await new Wkfg2TypeConverter().parse(this.metadata,async (table)=>await this._maybeSleepAtTable(table));
    }

    _registerMetaTables() {
        APP.metamodel.tables.forEach(metaTable => this.metaTableMap[metaTable.name] = metaTable);
    }

    _addStaticBackrefToMetamodel() {
        for (const table of APP.metamodel.tables) {
            const fkColumns = table.columns.filter(column => column.foreignKey != null && !this._fkNeverResolve(column));
            for (const column of fkColumns) {
                const foreignKeys = column.foreignKey.split(',').map(key => key.split('.'));
                column.foreignKeyParsed = [];
                for (let [fkTableName, fkColumnName] of foreignKeys) {
                    const fkTable = this.metaTableMap[fkTableName];
                    if (fkTable) {
                        const fkColumn = fkTable.columns.find(c=>c.name==fkColumnName && c.type!=='Backref');
                        column.foreignKeyParsed.push({
                            fkTable,
                            fkColumn
                        });
                        if (fkColumn && !fkTable.columns.some(c=>c.name==table.name)) {
                            fkTable.columns.push({
                                name: table.name,
                                type: "Backref",
                                table,
                                column,
                            });
                        }
                    }
                }
            }
        }
    }

    _addColumnMapToMetamodel() {
        for (const table of APP.metamodel.tables) {
            table.columnMap = {};
            for (const column of table.columns) {
                table.columnMap[column.name] = column;
            }
        }
    }

    _getPrimaryKeyName(tableName) {
        const name = this._primaryKeyOfTable[tableName];
        if (name == null) {
            console.log(this.getMetaTable(tableName));
            throw new Error('table ' + tableName + ' has no primary key');
        }
        return name;
    }

    _getTable(name) {
        return this.metadata[name] || [];
    }

    getMetaTable(name) {
        const metaTable = this.metaTableMap[name];
        if (metaTable == null) {
            throw new Error("unkown meta table " + name);
        }
        return metaTable;
    }

    _getTableKeys(tableName) {
        const metaTable = this.getMetaTable(tableName);
        const primary = metaTable.columns.find((c) => c.primaryKey);
        if (primary) {
            return [primary];
        }
        if (!Commons.isEmpty(metaTable.uniqueIndex)) {
            return metaTable.uniqueIndex.filter((s) => s != "App");
        }
    }

    _getFindMethod(tableName,fkColumn) {
        const rows = this._getTable(tableName);
        return key => rows.find(row => row[fkColumn] === key);
    }

    _fkNeverResolve(column) {
        return column.fkNeverResolve || this.getMetaTable(column.foreignKey.split('.')[0])?.fkNeverResolve;
    }

    _isOne2Many(column) {
        return column.multiselect || column.variantKey;
    }

    _forOneOrEach(objectOrArray) {
        if (Array.isArray(objectOrArray) || objectOrArray instanceof Set) {
            return (callback) => objectOrArray.forEach(callback);
        }
        return callback => callback(objectOrArray);
    }

    _lastTableTS=null;
    _sleepMsSum=0;
    async _maybeSleepAtTable(table) {
        if (this._lastTableTS==null) {
            this._lastTableTS = performance.now();
        } else {
            let now = performance.now();
            if (this._lastTableTS!=null && now-this._lastTableTS>ValidationMetadata.sleepAfterWorkMs) {
                await Commons.sleep(ValidationMetadata.sleepMs);
                this._sleepMsSum+=ValidationMetadata.sleepMs;
                this._lastTableTS = performance.now();
            }
        }
    }
}
